import type { ConsentManager, RenderableContent } from "./consent.js";
import type { ConsentDecisions } from "./types.js";

/**
 * Vanilla DOM banner. Single file, no framework, no shadow DOM (so the
 * controller can theme via plain CSS variables on the host page).
 *
 * Three render modes:
 *   * "banner"      — bottom-right floating card with Accept/Reject/Customise.
 *   * "preferences" — full-screen dialog with per-category toggles.
 *   * "closed"      — banner removed from DOM.
 *
 * GDPR notes baked in:
 *   * Reject All gets the same visual weight as Accept All (CNIL fines).
 *   * Required categories show as locked (disabled toggle) — never opt-out-able.
 *   * Sensitive captures (Art. 9) get a warning chip.
 *   * "Manage preferences" link stays available after a decision so the
 *     visitor can withdraw with one click (Art. 7(3)).
 */
const CONTAINER_ID = "mb-consent-root";
const STYLE_ID = "mb-consent-style";
const CUSTOM_STYLE_ID = "mb-consent-custom-style";

type Mode = "banner" | "preferences" | "closed";

export class ConsentBanner {
  private mount: HTMLElement;
  private manager: ConsentManager;
  private root: HTMLDivElement | null = null;
  private mode: Mode = "closed";
  private pendingDecisions: ConsentDecisions = {};

  constructor(manager: ConsentManager, mount: HTMLElement | null) {
    this.manager = manager;
    this.mount = mount ?? document.body;
  }

  showIfNeeded(): void {
    if (this.manager.needsDecision()) this.open("banner");
  }

  open(mode: Mode = "banner"): void {
    const content = this.manager.visibleContent();
    if (!content) return;
    this.pendingDecisions = { ...this.manager.get() };
    this.mode = mode;
    this.render(content);
  }

  close(): void {
    this.mode = "closed";
    if (this.root && this.root.parentNode) {
      this.root.parentNode.removeChild(this.root);
    }
    this.root = null;
  }

  private render(content: RenderableContent): void {
    // Only inject the built-in stylesheet when at least one render path needs
    // it. Custom banners / prefs can opt out by providing their own CSS.
    const willCustom =
      (this.mode === "banner" && this.shouldUseCustomBanner()) ||
      (this.mode === "preferences" && this.shouldUseCustomPrefs());
    if (!willCustom) {
      this.ensureStyle();
    }
    if (!this.root) {
      this.root = document.createElement("div");
      this.root.id = CONTAINER_ID;
      this.root.setAttribute("role", "dialog");
      this.root.setAttribute("aria-modal", "true");
      this.root.setAttribute("aria-label", content.content.bannerTitle);
      this.mount.appendChild(this.root);
    }
    this.root.innerHTML = "";
    if (this.mode === "banner") {
      if (this.shouldUseCustomBanner()) {
        this.root.className = "mb-c mb-c-banner mb-c-custom";
        this.renderCustomBanner(this.root);
      } else {
        this.root.className = "mb-c mb-c-banner";
        this.renderBanner(this.root, content);
      }
    } else if (this.mode === "preferences") {
      if (this.shouldUseCustomPrefs()) {
        this.root.className = "mb-c mb-c-prefs mb-c-custom";
        this.renderCustomPrefs(this.root, content);
      } else {
        this.root.className = "mb-c mb-c-prefs";
        this.renderPreferences(this.root, content);
      }
    }
  }

  private shouldUseCustomBanner(): boolean {
    const cb = this.manager.getPolicy()?.customBanner;
    return !!cb && cb.enabled && typeof cb.html === "string" && cb.html.trim().length > 0;
  }

  private shouldUseCustomPrefs(): boolean {
    const cb = this.manager.getPolicy()?.customBanner;
    return !!cb && cb.enabled && typeof cb.prefsHtml === "string" && cb.prefsHtml.trim().length > 0;
  }

  /**
   * Owner-provided HTML + CSS path. The HTML is injected as-is (innerHTML)
   * inside the analytics root; the CSS lives in a sibling <style> tag the
   * SDK manages. Elements carrying any of:
   *
   *   data-mb-accept-all     -> ConsentManager.acceptAll
   *   data-mb-reject-all     -> ConsentManager.rejectAll
   *   data-mb-customize      -> opens the preferences view
   *
   * get a click handler bound to them. Customers can mark multiple elements
   * (e.g. multiple "Accept" buttons in different layouts) — every match
   * receives the binding.
   */
  private renderCustomBanner(root: HTMLElement): void {
    const cb = this.manager.getPolicy()?.customBanner;
    if (!cb) return;
    this.applyCustomCss(cb.css);
    root.innerHTML = cb.html;
    const bindAll = (selector: string, handler: (e: Event) => void) => {
      root.querySelectorAll(selector).forEach((node) => {
        node.addEventListener("click", (e) => {
          e.preventDefault();
          handler(e);
        });
      });
    };
    bindAll("[data-mb-accept-all]", () => {
      this.manager.acceptAll("banner");
      this.close();
    });
    bindAll("[data-mb-reject-all]", () => {
      this.manager.rejectAll("banner");
      this.close();
    });
    bindAll("[data-mb-customize]", () => {
      this.open("preferences");
    });
  }

  /**
   * Owner-provided preferences view. The HTML is injected as-is and the
   * SDK binds:
   *
   *   data-mb-category="<key>"      -> the checkbox input for that category
   *                                    (element itself if it's an <input>,
   *                                    otherwise the first input descendant).
   *                                    Required categories are forced
   *                                    checked + disabled.
   *   data-mb-save-preferences      -> commit pending decisions, close view
   *   data-mb-accept-all            -> accept all, close
   *   data-mb-reject-all            -> reject non-required, close
   *   data-mb-close-preferences     -> close without saving
   */
  private renderCustomPrefs(root: HTMLElement, c: RenderableContent): void {
    const cb = this.manager.getPolicy()?.customBanner;
    if (!cb || !cb.prefsHtml) return;
    this.applyCustomCss(cb.prefsCss ?? "");
    root.innerHTML = cb.prefsHtml;

    const pending = this.pendingDecisions;
    const categories = c.categoryRules.map((g) => g.category);

    root.querySelectorAll<HTMLElement>("[data-mb-category]").forEach((node) => {
      const key = node.getAttribute("data-mb-category");
      if (!key) return;
      const cat = categories.find((c) => c.key === key);
      if (!cat) return;
      // Accept the input directly, or the first descendant input.
      const input =
        node instanceof HTMLInputElement && node.type === "checkbox"
          ? node
          : node.querySelector<HTMLInputElement>('input[type="checkbox"]');
      if (!input) return;
      const initial = cat.required ? true : Boolean(pending[key] ?? cat.defaultState);
      input.checked = initial;
      pending[key] = initial;
      if (cat.required) {
        input.disabled = true;
        node.setAttribute("data-mb-required", "true");
      }
      input.addEventListener("change", () => {
        if (cat.required) {
          input.checked = true;
          return;
        }
        pending[key] = input.checked;
      });
    });

    const bindAll = (selector: string, handler: (e: Event) => void) => {
      root.querySelectorAll(selector).forEach((node) => {
        node.addEventListener("click", (e) => {
          e.preventDefault();
          handler(e);
        });
      });
    };
    bindAll("[data-mb-save-preferences]", () => {
      this.manager.record(this.pendingDecisions, "preferences");
      this.close();
    });
    bindAll("[data-mb-accept-all]", () => {
      this.manager.acceptAll("preferences");
      this.close();
    });
    bindAll("[data-mb-reject-all]", () => {
      this.manager.rejectAll("preferences");
      this.close();
    });
    bindAll("[data-mb-close-preferences]", () => {
      this.close();
    });
  }

  private applyCustomCss(css: string): void {
    let style = document.getElementById(CUSTOM_STYLE_ID) as HTMLStyleElement | null;
    if (!css.trim()) {
      if (style && style.parentNode) style.parentNode.removeChild(style);
      return;
    }
    if (!style) {
      style = document.createElement("style");
      style.id = CUSTOM_STYLE_ID;
      document.head.appendChild(style);
    }
    style.textContent = css;
  }

  private renderBanner(root: HTMLElement, c: RenderableContent): void {
    const card = el("div", { class: "mb-c-card" });
    card.appendChild(el("h2", { class: "mb-c-title" }, c.content.bannerTitle));
    card.appendChild(el("p", { class: "mb-c-body" }, c.content.bannerBody));

    // "Data we collect" — dynamic. Lists each capture rule grouped by
    // category. Adding a new capture rule in the dashboard surfaces here
    // automatically after the SDK refetches the policy.
    if (c.categoryRules.some((g) => g.captures.length > 0)) {
      const summary = el("details", { class: "mb-c-summary" });
      const sumLabel = el("summary", {}, c.content.language === "tr" ? "Toplanan veriler" : "Data we collect");
      summary.appendChild(sumLabel);
      for (const g of c.categoryRules) {
        if (g.captures.length === 0) continue;
        const group = el("div", { class: "mb-c-group" });
        group.appendChild(el("strong", {}, g.category.label));
        const ul = el("ul", { class: "mb-c-props" });
        for (const cap of g.captures) {
          const li = el("li", {}, cap.property);
          if (cap.sensitive) {
            li.appendChild(el("span", { class: "mb-c-chip" }, c.content.language === "tr" ? "Hassas" : "Sensitive"));
          }
          ul.appendChild(li);
        }
        group.appendChild(ul);
        summary.appendChild(group);
      }
      card.appendChild(summary);
    }

    const actions = el("div", { class: "mb-c-actions" });
    // Order matters: Reject must appear first OR be visually equal — we
    // place Reject left, Customise middle, Accept right; all same style.
    actions.appendChild(button(c.content.rejectAllLabel, "mb-c-btn mb-c-btn-secondary", () => {
      this.manager.rejectAll("banner");
      this.close();
    }));
    actions.appendChild(button(c.content.customizeLabel, "mb-c-btn mb-c-btn-secondary", () => {
      this.open("preferences");
    }));
    actions.appendChild(button(c.content.acceptAllLabel, "mb-c-btn mb-c-btn-primary", () => {
      this.manager.acceptAll("banner");
      this.close();
    }));
    card.appendChild(actions);

    if (c.content.controller.privacyPolicyUrl) {
      const a = el("a", {
        class: "mb-c-link",
        href: c.content.controller.privacyPolicyUrl,
        target: "_blank",
        rel: "noopener noreferrer",
      }, c.content.language === "tr" ? "Gizlilik Politikası" : "Privacy policy");
      card.appendChild(a);
    }

    root.appendChild(card);
  }

  private renderPreferences(root: HTMLElement, c: RenderableContent): void {
    const overlay = el("div", { class: "mb-c-overlay" });
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) this.open("banner");
    });

    const card = el("div", { class: "mb-c-card mb-c-card-prefs" });
    card.appendChild(el("h2", { class: "mb-c-title" }, c.content.bannerTitle));
    card.appendChild(el("p", { class: "mb-c-body" }, c.content.bannerBody));

    const list = el("div", { class: "mb-c-cats" });
    for (const g of c.categoryRules) {
      const row = el("div", { class: "mb-c-cat" });
      const head = el("div", { class: "mb-c-cat-head" });
      head.appendChild(el("strong", {}, g.category.label));

      const toggle = el("label", { class: "mb-c-toggle" }) as HTMLLabelElement;
      const input = document.createElement("input");
      input.type = "checkbox";
      input.checked = g.category.required ? true : Boolean(this.pendingDecisions[g.category.key]);
      input.disabled = g.category.required;
      input.addEventListener("change", () => {
        this.pendingDecisions[g.category.key] = input.checked;
      });
      const slider = el("span", { class: "mb-c-slider" });
      toggle.appendChild(input);
      toggle.appendChild(slider);
      head.appendChild(toggle);
      row.appendChild(head);

      row.appendChild(el("p", { class: "mb-c-cat-desc" }, g.category.description));

      if (g.captures.length > 0) {
        const props = el("div", { class: "mb-c-cat-props" });
        for (const cap of g.captures) {
          const chip = el("span", { class: "mb-c-prop" }, cap.property);
          if (cap.sensitive) chip.classList.add("mb-c-prop-sensitive");
          props.appendChild(chip);
        }
        row.appendChild(props);
      }
      list.appendChild(row);
    }
    card.appendChild(list);

    if (c.content.subprocessors.length > 0) {
      const subDetails = el("details", { class: "mb-c-summary" });
      subDetails.appendChild(el("summary", {}, c.content.language === "tr" ? "Alt işleyiciler" : "Subprocessors"));
      const ul = el("ul", { class: "mb-c-subprocs" });
      for (const sp of c.content.subprocessors) {
        const li = el("li", {});
        li.appendChild(el("strong", {}, sp.name));
        if (sp.countryCode) li.appendChild(el("span", { class: "mb-c-chip" }, sp.countryCode));
        li.appendChild(el("span", { class: "mb-c-purpose" }, ` — ${sp.purpose}`));
        ul.appendChild(li);
      }
      subDetails.appendChild(ul);
      card.appendChild(subDetails);
    }

    const meta = el("p", { class: "mb-c-meta" },
      c.content.language === "tr"
        ? `Verileriniz ${c.content.retentionDays} gün saklanır. Sorularınız için: ${c.content.controller.contactEmail}`
        : `Data is retained for ${c.content.retentionDays} days. Contact: ${c.content.controller.contactEmail}`,
    );
    card.appendChild(meta);

    const actions = el("div", { class: "mb-c-actions" });
    actions.appendChild(button(c.content.rejectAllLabel, "mb-c-btn mb-c-btn-secondary", () => {
      this.manager.rejectAll("preferences");
      this.close();
    }));
    actions.appendChild(button(c.content.language === "tr" ? "Seçimi kaydet" : "Save preferences", "mb-c-btn mb-c-btn-primary", () => {
      this.manager.record(this.pendingDecisions, "preferences");
      this.close();
    }));
    actions.appendChild(button(c.content.acceptAllLabel, "mb-c-btn mb-c-btn-primary", () => {
      this.manager.acceptAll("preferences");
      this.close();
    }));
    card.appendChild(actions);

    overlay.appendChild(card);
    root.appendChild(overlay);
  }

  private ensureStyle(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = CSS;
    document.head.appendChild(style);
  }
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === "class") node.className = v;
    else node.setAttribute(k, v);
  }
  if (text !== undefined) node.appendChild(document.createTextNode(text));
  return node;
}

function button(label: string, className: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = className;
  b.textContent = label;
  b.addEventListener("click", onClick);
  return b;
}

/**
 * Inlined as a string so the IIFE bundle has zero external CSS dependencies.
 * Uses CSS custom properties so the controller can override via `:root { --mb-c-bg: ... }`.
 */
const CSS = `
#mb-consent-root, #mb-consent-root * { box-sizing: border-box; }
#mb-consent-root {
  --mb-c-bg: #ffffff;
  --mb-c-fg: #0f172a;
  --mb-c-muted: #475569;
  --mb-c-border: #e2e8f0;
  --mb-c-primary: #0f766e;
  --mb-c-primary-fg: #ffffff;
  --mb-c-secondary-bg: #f8fafc;
  --mb-c-overlay: rgba(15, 23, 42, 0.45);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  color: var(--mb-c-fg);
}
@media (prefers-color-scheme: dark) {
  #mb-consent-root {
    --mb-c-bg: #1e293b;
    --mb-c-fg: #f1f5f9;
    --mb-c-muted: #cbd5e1;
    --mb-c-border: #334155;
    --mb-c-secondary-bg: #0f172a;
  }
}
.mb-c-banner { position: fixed; right: 16px; bottom: 16px; z-index: 2147483600; max-width: 420px; }
.mb-c-banner .mb-c-card { background: var(--mb-c-bg); border: 1px solid var(--mb-c-border); border-radius: 10px; padding: 18px; box-shadow: 0 10px 30px rgba(15, 23, 42, 0.18); }
.mb-c-prefs .mb-c-overlay { position: fixed; inset: 0; background: var(--mb-c-overlay); z-index: 2147483600; display: flex; align-items: center; justify-content: center; padding: 16px; }
.mb-c-card-prefs { background: var(--mb-c-bg); border-radius: 12px; padding: 22px; max-width: 560px; width: 100%; max-height: 85vh; overflow: auto; }
.mb-c-title { font-size: 16px; font-weight: 600; margin: 0 0 8px; }
.mb-c-body { font-size: 13px; color: var(--mb-c-muted); margin: 0 0 12px; line-height: 1.5; }
.mb-c-actions { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 12px; }
.mb-c-btn { flex: 1; min-width: 110px; padding: 9px 12px; border: 1px solid var(--mb-c-border); border-radius: 6px; font-size: 13px; font-weight: 600; cursor: pointer; line-height: 1.2; }
.mb-c-btn-primary { background: var(--mb-c-primary); color: var(--mb-c-primary-fg); border-color: var(--mb-c-primary); }
.mb-c-btn-secondary { background: var(--mb-c-secondary-bg); color: var(--mb-c-fg); }
.mb-c-btn:hover { filter: brightness(0.95); }
.mb-c-link { display: inline-block; margin-top: 10px; font-size: 12px; color: var(--mb-c-muted); text-decoration: underline; }
.mb-c-summary { margin-top: 10px; font-size: 12px; color: var(--mb-c-muted); }
.mb-c-summary > summary { cursor: pointer; padding: 4px 0; }
.mb-c-group { margin: 8px 0; }
.mb-c-props { list-style: none; padding: 0; margin: 4px 0 0; display: flex; flex-wrap: wrap; gap: 4px; }
.mb-c-props li { background: var(--mb-c-secondary-bg); padding: 2px 7px; border-radius: 999px; font-size: 11px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.mb-c-chip { display: inline-block; background: #f59e0b; color: #fff; font-size: 10px; font-weight: 600; padding: 1px 6px; border-radius: 999px; margin-left: 6px; }
.mb-c-cats { display: flex; flex-direction: column; gap: 12px; margin: 10px 0; }
.mb-c-cat { background: var(--mb-c-secondary-bg); padding: 12px; border-radius: 8px; border: 1px solid var(--mb-c-border); }
.mb-c-cat-head { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.mb-c-cat-desc { font-size: 12px; color: var(--mb-c-muted); margin: 6px 0 0; line-height: 1.5; }
.mb-c-cat-props { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 8px; }
.mb-c-prop { background: var(--mb-c-bg); border: 1px solid var(--mb-c-border); padding: 2px 7px; border-radius: 999px; font-size: 11px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.mb-c-prop-sensitive { border-color: #f59e0b; color: #d97706; }
.mb-c-toggle { position: relative; display: inline-block; width: 36px; height: 20px; }
.mb-c-toggle input { opacity: 0; width: 0; height: 0; }
.mb-c-slider { position: absolute; cursor: pointer; inset: 0; background: var(--mb-c-border); border-radius: 999px; transition: 0.2s; }
.mb-c-slider:before { content: ""; position: absolute; height: 14px; width: 14px; left: 3px; bottom: 3px; background: var(--mb-c-bg); border-radius: 50%; transition: 0.2s; }
.mb-c-toggle input:checked + .mb-c-slider { background: var(--mb-c-primary); }
.mb-c-toggle input:checked + .mb-c-slider:before { transform: translateX(16px); }
.mb-c-toggle input:disabled + .mb-c-slider { opacity: 0.6; cursor: not-allowed; }
.mb-c-meta { font-size: 11px; color: var(--mb-c-muted); margin: 10px 0; line-height: 1.5; }
.mb-c-subprocs { list-style: none; padding: 0; margin: 4px 0 0; }
.mb-c-subprocs li { padding: 4px 0; font-size: 12px; }
.mb-c-purpose { color: var(--mb-c-muted); }
@media (max-width: 480px) {
  .mb-c-banner { left: 8px; right: 8px; bottom: 8px; max-width: none; }
}
`;
